import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Home, ClipboardList, BookOpen, MessageCircleHeart, Feather,
  TrendingUp, HandHeart, Users, Sparkles, Search, ChevronDown, ArrowRight,
  ArrowDown, PlayCircle, HelpCircle, HeartHandshake, MessageCircle, LifeBuoy,
  ShieldCheck, Coffee,
} from 'lucide-react';

// ─── Brotherhood+ MEMBER content ─────────────────────────────────────────────
const MEMBER_HERO = {
  title: 'How to use Brotherhood+.',
  subtitle: null,
  body: 'Read this once. Come back anytime. Every feature explained in plain language, plus a searchable FAQ and a visual map of how it all fits together.',
  searchPlaceholder: "Search help — try 'AI', 'journal', 'progress', 'therapy'…",
};

const MEMBER_FEATURES = [
  { icon: Home, title: 'Home', desc: 'Your personalized dashboard. Every day it recommends your next best step.' },
  { icon: ClipboardList, title: 'Daily Check-In', desc: 'Under one minute a day. Consistency fuels every other recommendation.' },
  { icon: BookOpen, title: 'Brotherhood University', desc: 'Evidence-based lessons built by licensed clinicians.' },
  { icon: MessageCircleHeart, title: 'AI Coach', desc: 'A private, warm coach available 24/7. Apply what you learned.' },
  { icon: Feather, title: 'Journal', desc: 'Private reflection space with optional AI reflections.' },
  { icon: TrendingUp, title: 'Progress', desc: 'Track wellness trends, streaks, and weekly reviews.' },
  { icon: HandHeart, title: 'Support', desc: 'Guidance for loved ones supporting the men they care about.' },
  { icon: Users, title: 'Find Therapy', desc: 'Culturally responsive licensed therapists you can filter by state, insurance, specialty, and language.' },
];

const MEMBER_FLOW = [
  { icon: ClipboardList, label: 'Daily Check-In', href: '/app/checkin', note: 'How are you doing today?' },
  { icon: Sparkles, label: 'Personalized Recommendations', href: '/app', note: 'Home shows your next best step.' },
  { icon: BookOpen, label: 'Lessons', href: '/app/learn', note: 'Learn the concept.' },
  { icon: MessageCircleHeart, label: 'AI Coach', href: '/app/coach', note: 'Apply it to your life.' },
  { icon: Feather, label: 'Journal', href: '/app/journal', note: 'Reflect on what shifted.' },
  { icon: TrendingUp, label: 'Progress', href: '/app/progress', note: 'See patterns emerge.' },
];

const MEMBER_FAQS = [
  { q: 'Where should I start?', a: 'Begin with a Daily Check-In. It takes under a minute and unlocks personalized recommendations and AI Coach context.', tags: ['start','begin','first day','onboarding','check-in'] },
  { q: 'How should I use Brotherhood+ each day?', a: 'A great daily rhythm is 3–5 minutes: (1) 60-second Check-In, (2) whatever your Home dashboard recommends for today (a lesson, coaching conversation, or journal prompt). Consistency compounds.', tags: ['daily','routine','rhythm','time'] },
  { q: 'What does the AI Coach remember?', a: "The AI Coach uses your recent check-ins, lessons completed, journal reflections you\u2019ve chosen to share with it, and prior chat history to give context-aware coaching. Your data stays yours — you can export or delete it anytime from Profile.", tags: ['coach','ai','memory','privacy','personalization'] },
  { q: 'How do Growth Plans work?', a: "A Growth Plan is a multi-week sequence of lessons focused on one theme (e.g. anger regulation, fatherhood, boundaries). Home tracks your active plan and the AI Coach knows what you\u2019re working on.", tags: ['growth plan','plan','multi-week','goals'] },
  { q: 'What is the Weekly Wellness Review?', a: 'Every 7 days of check-ins, Brotherhood+ generates a personalized AI Wellness Review — patterns, wins, and one small next step. It lives in the Progress tab.', tags: ['weekly review','wellness review','progress','insights'] },
  { q: 'How are recommendations personalized?', a: 'Recommendations blend your check-in data (mood, stress, sleep, energy), your journal signal, and where you are in an active Growth Plan. The more consistent your check-ins, the sharper the recommendation.', tags: ['recommendations','personalization','algorithm'] },
  { q: 'Is Brotherhood+ therapy?', a: "No. Brotherhood+ is a wellness and coaching platform grounded in evidence-based frameworks (CBT, ACT, DBT, MI, Positive Psychology). It is not a substitute for a licensed therapist. If you\u2019re in a crisis, call 988 (US) or use Find Therapy to locate a professional.", tags: ['therapy','clinical','crisis','safety'] },
  { q: 'How can I get the best results?', a: '1) Check in daily (60 seconds). 2) Follow the Home recommendation for that day. 3) Use the AI Coach when something is on your mind — even briefly. Consistency beats intensity.', tags: ['results','tips','best practices'] },
  { q: 'What is The Lounge?', a: "The Lounge is five short, replayable canvas games — Brotherhood Flyer (Flappy-style), Runner (endless runner), Rally (hill-climb), Hoops (basketball), and Drift (top-down racing). Each play earns Lounge Points and counts toward daily and weekly challenges. Strategic rest is part of the practice.", tags: ['lounge','games','arcade','break','fun'] },
];

const MEMBER_JUMPS = [
  { q: 'ai', href: '/app/coach', label: 'AI Coach' },
  { q: 'journal', href: '/app/journal', label: 'Journal' },
  { q: 'progress', href: '/app/progress', label: 'Progress' },
  { q: 'growth', href: '/app', label: 'Growth Plans (Home)' },
  { q: 'therapy', href: '/app/therapists', label: 'Find Therapy' },
  { q: 'checkin', href: '/app/checkin', label: 'Daily Check-In' },
  { q: 'support', href: '/app/support', label: 'Support' },
  { q: 'recommend', href: '/app', label: 'Recommendations (Home)' },
];

// ─── Brotherhood Support SUPPORTER content ───────────────────────────────────
const SUPPORTER_HERO = {
  title: 'How to use Brotherhood Support',
  subtitle: 'Guidance for the people who care.',
  body: 'Brotherhood Support helps partners, parents, siblings, friends, mentors, pastors, and coaches support the men in their lives with wisdom, patience, healthy boundaries, and practical guidance.',
  searchPlaceholder: "Search support help — try \u201cboundaries,\u201d \u201cwhat to say,\u201d \u201ccrisis,\u201d \u201ctherapy,\u201d \u201canger,\u201d \u201cgrief\u201d\u2026",
};

const SUPPORTER_FEATURES = [
  { icon: Home, title: 'Support Home', desc: 'Your daily starting place for guidance and resources.' },
  { icon: MessageCircleHeart, title: 'Support Coach', desc: 'Private AI guidance for difficult conversations.' },
  { icon: BookOpen, title: 'Support Library', desc: 'Practical guides and evidence-informed articles.' },
  { icon: MessageCircle, title: 'Conversation Starters', desc: 'Learn healthier ways to begin meaningful conversations.' },
  { icon: LifeBuoy, title: 'Crisis Resources', desc: 'Know what to do when someone needs immediate help.' },
  { icon: ShieldCheck, title: 'Healthy Boundaries', desc: 'Support without carrying everything yourself.' },
  { icon: Coffee, title: 'Self-Care', desc: 'You matter too. Learn how to care for yourself while caring for someone else.' },
];

const SUPPORTER_FLOW = [
  { icon: Home, label: 'Support Home', href: '/app/support-home', note: 'Start every visit here.' },
  { icon: BookOpen, label: 'Read Practical Guides', href: '/app/support', note: 'Learn what helps — and what doesn\u2019t.' },
  { icon: MessageCircleHeart, label: 'Talk with Support Coach', href: '/app/support/coach', note: 'Think through a hard moment.' },
  { icon: HeartHandshake, label: 'Apply Conversations & Boundaries', href: '/app/support', note: 'Take one small, respectful step.' },
  { icon: Sparkles, label: 'Return for Ongoing Guidance', href: '/app/support-home', note: 'Support is a rhythm, not a project.' },
];

const SUPPORTER_FAQS = [
  { q: 'Is Brotherhood+ only for men of color?', a: 'No — Brotherhood+ is for men from every background. We built it with the experiences of men of color in mind because those perspectives have too often been overlooked in traditional wellness spaces. That intentionality isn\u2019t a boundary around who belongs. If you\u2019re a man committed to learning, reflecting, and growing, Brotherhood+ was built for you too.', tags: ['who','belong','inclusive','men of color','white','background','everyone'] },
  { q: 'What is Brotherhood Support?', a: 'Brotherhood Support is a subscription designed for the people who love and support the men in their lives — partners, parents, siblings, mentors, pastors, coaches, and friends. It provides an AI Support Coach, a Support Library of practical guides, and crisis resources. It is not designed to be used by the man himself.', tags: ['what','overview','about'] },
  { q: 'Where should I start?', a: 'Start on Support Home. Read one Practical Guide that matches what you\u2019re facing right now (a hard conversation, worry, grief, therapy resistance, or burnout). If you need to think through a specific situation, open the Support Coach.', tags: ['start','begin','first day'] },
  { q: 'How is the Support Coach different from a therapist?', a: 'The Support Coach is an educational tool — evidence-informed guidance for supporters. It does not diagnose, prescribe, or replace licensed therapy. If someone may be in immediate danger, call 911 or 988 immediately. For ongoing clinical care, Brotherhood Support is a helpful companion, not a substitute.', tags: ['coach','therapy','clinical','difference'] },
  { q: 'Can the person I\u2019m supporting see what I write here?', a: 'No. Your Support Coach conversations and any guides you save are private to your account. Brotherhood Support and Brotherhood+ are separate accounts — supporters cannot see the personal data of the men in their lives, and members cannot see yours.', tags: ['privacy','private','share'] },
  { q: 'How do I encourage therapy without pushing?', a: 'Lead with what you\u2019ve noticed and how it\u2019s affected you, not what he needs to fix. Offer options (a warm phone number, a directory, a therapist you\u2019ve already screened). Never ultimatum unless safety is at stake. The Support Coach can help you rehearse the exact wording.', tags: ['therapy','encourage','push','resistant'] },
  { q: 'What if he says he\u2019s "fine" but I don\u2019t believe him?', a: 'Trust what you\u2019re observing. Say what you\u2019ve seen (sleeping less, withdrawing, shorter fuse) — not what you\u2019ve concluded. Ask an open question you actually want the answer to. Then be quiet long enough for a real reply. Open a Support Coach session and describe what you\u2019re seeing — it will help you find the right words.', tags: ['fine','withdrawn','denial'] },
  { q: 'How do I set a healthy boundary without abandoning him?', a: 'A healthy boundary describes what you will and won\u2019t do — not what he must do. "I love you and I\u2019m here on Sunday nights" is different from "you have to stop drinking." Boundaries protect the relationship long-term, not punish him short-term. Read the Boundaries guide in the Support Library.', tags: ['boundaries','limits','self-care'] },
  { q: 'What should I do in a crisis?', a: 'If there is immediate danger, call 911. Otherwise, dial or text 988 (Suicide & Crisis Lifeline) — free, confidential, 24/7. For veterans, dial 988 then press 1. For intimate partner violence, the National Domestic Violence Hotline is 1-800-799-7233. Stay with him if it\u2019s safe. Never promise secrecy about a life-threatening situation.', tags: ['crisis','988','emergency','danger','suicide'] },
  { q: 'How do I take care of myself while supporting him?', a: 'Caring for a person in pain is real work. You need sleep, food, movement, at least one confidant of your own, and permission to say "not tonight." Read the Self-Care guide in the Support Library — burnout in supporters is common and often invisible.', tags: ['self-care','burnout','exhausted','helper'] },
];

const SUPPORTER_JUMPS = [
  { q: 'coach', href: '/app/support/coach', label: 'Support Coach' },
  { q: 'library', href: '/app/support', label: 'Support Library' },
  { q: 'crisis', href: '/app/support/sup_crisis', label: 'Crisis Resources' },
  { q: 'boundaries', href: '/app/support', label: 'Healthy Boundaries' },
  { q: 'therapy', href: '/app/support', label: 'Encouraging Therapy' },
  { q: 'anger', href: '/app/support', label: 'Anger & Stress' },
  { q: 'grief', href: '/app/support', label: 'Grief Support' },
  { q: 'conversation', href: '/app/support', label: 'Conversation Starters' },
  { q: 'self-care', href: '/app/support', label: 'Self-Care' },
];

export default function HelpCenter() {
  const nav = useNavigate();
  const { hash } = useLocation();
  const { user } = useAuth() || {};
  const isSupporter = (user?.role || 'member') === 'supporter';

  const HERO = isSupporter ? SUPPORTER_HERO : MEMBER_HERO;
  const FEATURES = isSupporter ? SUPPORTER_FEATURES : MEMBER_FEATURES;
  const FLOW = isSupporter ? SUPPORTER_FLOW : MEMBER_FLOW;
  const FAQS = isSupporter ? SUPPORTER_FAQS : MEMBER_FAQS;
  const SEARCH_JUMPS = isSupporter ? SUPPORTER_JUMPS : MEMBER_JUMPS;

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);

  const replayTour = () => window.dispatchEvent(new CustomEvent('bp:replay-tour'));

  const filteredFaqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.filter(f =>
      f.q.toLowerCase().includes(q) ||
      f.a.toLowerCase().includes(q) ||
      f.tags.some(t => t.toLowerCase().includes(q))
    );
  }, [query, FAQS]);

  const matchedJumps = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return SEARCH_JUMPS.filter(j => j.q.includes(q) || j.label.toLowerCase().includes(q));
  }, [query, SEARCH_JUMPS]);

  return (
    <div className="px-5 lg:px-10 pt-6 lg:pt-12 pb-16 space-y-10 lg:space-y-14" data-testid={isSupporter ? 'help-center-supporter' : 'help-center'}>
      {/* HERO */}
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">
          <HelpCircle size={12} strokeWidth={1.75}/>Help Center
        </div>
        <h1 className="font-heading text-4xl lg:text-6xl mt-3 lg:mt-4 leading-[1.05] max-w-4xl text-navy" data-testid="help-hero-title">
          {HERO.title}
        </h1>
        {HERO.subtitle && (
          <p className="font-heading text-xl lg:text-2xl text-navy/70 mt-2 lg:mt-3 italic" data-testid="help-hero-subtitle">
            {HERO.subtitle}
          </p>
        )}
        <p className="text-navy/60 text-base lg:text-xl mt-4 lg:mt-6 max-w-3xl leading-relaxed">
          {HERO.body}
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <button
            data-testid="help-replay-tour" onClick={replayTour}
            className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-soft transition-smooth"
          >
            <PlayCircle size={14} strokeWidth={1.75}/>Replay the guided tour
          </button>
        </div>
      </div>

      {/* SEARCH */}
      <section>
        <div className="relative max-w-2xl">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/40"/>
          <input
            data-testid="help-search"
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={HERO.searchPlaceholder}
            className="w-full bg-white border border-navy/10 rounded-2xl pl-11 pr-4 py-4 text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-gold"
          />
        </div>
        {matchedJumps.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="help-search-jumps">
            {matchedJumps.map(j => (
              <button
                key={j.href + j.label} onClick={() => nav(j.href)}
                className="inline-flex items-center gap-1.5 text-xs bg-white border border-navy/10 rounded-full px-3 py-1.5 hover:border-navy/40 transition-smooth"
              >Jump to {j.label} <ArrowRight size={11}/></button>
            ))}
          </div>
        )}
      </section>

      {/* GETTING STARTED */}
      <section>
        <p className="font-heading text-xl lg:text-2xl text-navy mb-5">Getting Started</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-testid="help-features-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="bg-white rounded-2xl p-5 lg:p-6 border border-navy/5 transition-smooth hover:-translate-y-0.5" style={{ boxShadow: '0 4px 20px rgba(11,25,44,0.04)' }}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(197,160,89,0.14)' }}>
                  <f.icon size={17} className="text-gold" strokeWidth={1.5}/>
                </div>
                <div>
                  <p className="font-heading text-lg text-navy leading-snug">{f.title}</p>
                  <p className="text-sm text-navy/60 mt-1 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS — flow diagram */}
      <section>
        <p className="font-heading text-xl lg:text-2xl text-navy mb-2">
          {isSupporter ? 'How Brotherhood Support works' : 'How Brotherhood+ works'}
        </p>
        <p className="text-sm text-navy/55 mb-6 max-w-2xl">
          {isSupporter
            ? 'Brotherhood Support gives you guidance, education, and coaching — not a personal wellness dashboard. These are the steps that turn worry into a plan.'
            : 'Each tool builds on the previous one. You don\u2019t have to use everything — but the ones that stack together are where the real change happens.'}
        </p>
        <div className="bg-white rounded-3xl p-6 lg:p-10 border border-navy/5" style={{ boxShadow: '0 8px 30px rgba(11,25,44,0.05)' }}>
          <ol className="space-y-4 lg:space-y-5">
            {FLOW.map((f, i) => (
              <li key={f.label} data-testid={`help-flow-${i}`} className="flex flex-col">
                <button onClick={() => nav(f.href)} className="group flex items-start gap-4 text-left w-full">
                  <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(11,25,44,0.05)' }}>
                    <f.icon size={18} className="text-navy" strokeWidth={1.5}/>
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="font-heading text-base lg:text-lg text-navy leading-snug group-hover:text-gold transition-smooth">{f.label}</span>
                    <span className="block text-sm text-navy/55 mt-0.5 leading-relaxed">{f.note}</span>
                  </span>
                  <ArrowRight size={14} className="text-navy/30 mt-3.5 group-hover:text-navy transition-smooth"/>
                </button>
                {i < FLOW.length - 1 && (
                  <span className="ml-5 my-1 text-navy/25" aria-hidden><ArrowDown size={14} strokeWidth={1.5}/></span>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq">
        <p className="font-heading text-xl lg:text-2xl text-navy mb-2">Frequently Asked Questions</p>
        <p className="text-sm text-navy/55 mb-5">{filteredFaqs.length} of {FAQS.length} shown{query.trim() ? ` \u2022 filtering by "${query}"` : ''}</p>
        <div className="space-y-2.5">
          {filteredFaqs.map((f, i) => {
            const isOpen = expanded === i;
            return (
              <div key={f.q} className="bg-white rounded-2xl border border-navy/5" data-testid={`help-faq-${i}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : i)}
                  className="w-full text-left flex items-start justify-between gap-4 p-5 lg:p-6"
                >
                  <span className="font-heading text-base lg:text-lg text-navy leading-snug">{f.q}</span>
                  <ChevronDown size={16} className={`text-navy/40 shrink-0 mt-1 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}/>
                </button>
                {isOpen && (
                  <div className="px-5 lg:px-6 pb-5 lg:pb-6">
                    <p className="text-sm lg:text-base text-navy/70 leading-relaxed whitespace-pre-line">{f.a}</p>
                  </div>
                )}
              </div>
            );
          })}
          {filteredFaqs.length === 0 && (
            <p className="text-sm text-navy/50 italic">No matches. Try a shorter word or browse the categories above.</p>
          )}
        </div>
      </section>

      {/* CONTACT & FEEDBACK */}
      <section data-testid="help-contact">
        <div className="rounded-3xl bg-navy text-white p-6 lg:p-10" style={{ boxShadow: '0 8px 30px rgba(11,25,44,0.15)' }}>
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Still need a hand?</p>
          <h2 className="font-heading text-2xl lg:text-3xl mt-2 leading-snug">Talk to a real person.</h2>
          <p className="text-white/70 text-sm lg:text-base mt-3 max-w-2xl leading-relaxed">
            Reach the Brotherhood+ team directly, or send quick feedback about anything you noticed. We usually reply within one business day.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button
              data-testid="help-send-feedback"
              onClick={() => window.dispatchEvent(new CustomEvent('bp:open-feedback'))}
              className="inline-flex items-center gap-2 bg-gold text-navy rounded-full px-5 py-2.5 text-sm font-semibold hover:brightness-95 transition-smooth"
            >
              <MessageCircle size={15} strokeWidth={2}/>Send feedback
            </button>
            <Link
              to="/support"
              data-testid="help-contact-support"
              className="inline-flex items-center gap-2 bg-white/10 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-white/20 transition-smooth"
            >
              <LifeBuoy size={15} strokeWidth={1.75}/>Contact support
            </Link>
            <a
              href="mailto:brotherhoodplusapp@gmail.com"
              data-testid="help-email"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-smooth"
            >
              brotherhoodplusapp@gmail.com
            </a>
          </div>
        </div>
      </section>

      <p className="text-xs text-navy/40 text-center leading-relaxed max-w-2xl mx-auto">
        You can also use the floating Feedback button in the bottom-right of any page.
      </p>
    </div>
  );
}
